import { useEffect, useMemo, useState } from "react";
import { MuiMaterials } from "../../@index.js";
import { createWindowChromeTheme } from "./windowChromeTheme.js";
import SiteWindowTitle from "./SiteWindowTitle.js";
import SiteWindowHeaderLeading, { SiteWindowToolNav } from "./SiteWindowHeaderLeading.js";
import ChromeHeaderBar from "./ChromeHeaderBar.js";

/**
 * Site Setup–style chrome for the main-menu modals (Site Comparison, Site Filter,
 * Site Grades, Site Inventory, Site Issues, Site Versioning, Profile). Renders inside an
 * MUI Modal so it keeps the backdrop, focus trap, and escape-to-close behavior those
 * modals had before, but the interior is the same fixed-inset page-bg + header row +
 * body-slot layout that WindowChrome uses for full-window pages.
 *
 * Callers control the modal state with an `open` prop and receive close events via
 * `onClose`. The typical binding is `open={isModalOpen[0]} onClose={() => setIsModalOpen([false])}`.
 */
export default function ModalChrome({
    open,
    onClose,
    title,
    /** Short tools-menu label (e.g. "Comparison"). Defaults to `title`. */
    toolTitle = null,
    subtitle = null,
    actions = null,
    children,
    closeAriaLabel = "Close",
    /** Extra props forwarded to the outer <Modal> (e.g. `keepMounted`). */
    modalProps = null,
    /** Optional status banner, mirrors Site Setup's status area. */
    statusBanner = null,
    /** Optional loading overlay centered in the viewing area (Inventory, Versioning, etc.). */
    loadingOverlay = null,
    /** Extra sx for the modal backdrop (the translucent layer). */
    backdropSx = null,
    /** Class on the interior overlay div so existing CSS selectors keep working. */
    className,
    /** When set, the title becomes a Site XXXX switcher menu. */
    siteWindowId = null,
    /** Current site GUID to carry into the next Site XXXX window. */
    siteGuid = null,
    /** When true with onHeaderSiteChange, show SiteHeaderSiteSelect + divider. */
    showHeaderSiteSelect = false,
    onHeaderSiteChange = null,
    headerSites = null,
    headerFilterSite = null,
    headerSiteSelectDisabled = false,
    /** Show an "All Sites" row in the header site picker (Versioning). */
    headerIncludeAllSites = false,
}) {
    const [themeVersion, setThemeVersion] = useState(0);

    useEffect(() => {
        const onTheme = () => setThemeVersion((prev) => prev + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    const { textPrimary, textSecondary, paper, border, pageBg, theme, isLight } = useMemo(
        () => createWindowChromeTheme(),
        [themeVersion],
    );

    const resolvedToolTitle = toolTitle || title;
    const titleId = typeof resolvedToolTitle === "string" ? `modal-title-${resolvedToolTitle}` : undefined;
    const useSiteToolHeader = !!siteWindowId;
    const showSite = useSiteToolHeader
        && !!showHeaderSiteSelect
        && typeof onHeaderSiteChange === "function";

    const leading = useSiteToolHeader ? (
        showSite ? (
            <SiteWindowHeaderLeading
                toolTitle={resolvedToolTitle}
                siteWindowId={siteWindowId}
                siteGuid={siteGuid}
                textSecondary={textSecondary}
                border={border}
                showSiteSelect
                siteSelectValue={siteGuid || ""}
                onSiteChange={onHeaderSiteChange}
                sites={headerSites}
                filterSite={headerFilterSite}
                siteSelectDisabled={headerSiteSelectDisabled}
                includeAllSites={headerIncludeAllSites}
                titleId={titleId}
                includeTool={false}
            />
        ) : null
    ) : (
        <SiteWindowTitle
            title={title}
            subtitle={subtitle}
            siteWindowId={siteWindowId}
            siteGuid={siteGuid}
            textSecondary={textSecondary}
            titleId={titleId}
        />
    );

    const tool = useSiteToolHeader ? (
        <SiteWindowToolNav
            toolTitle={resolvedToolTitle}
            siteWindowId={siteWindowId}
            siteGuid={siteGuid}
            textSecondary={textSecondary}
            titleId={titleId}
        />
    ) : null;

    return (
        <MuiMaterials.Modal
            open={!!open}
            onClose={onClose}
            aria-labelledby={titleId}
            sx={{
                backgroundColor: isLight ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.45)",
                ...(backdropSx || {}),
            }}
            {...(modalProps || {})}
        >
            <MuiMaterials.ThemeProvider theme={theme}>
                <div
                    className={className}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1000,
                        display: "flex",
                        flexDirection: "column",
                        background: pageBg,
                        color: textPrimary,
                        paddingTop: "env(safe-area-inset-top, 0px)",
                        paddingBottom: "env(safe-area-inset-bottom, 0px)",
                    }}
                >
                    <ChromeHeaderBar
                        leading={leading}
                        tool={tool}
                        actions={actions}
                        onClose={onClose}
                        closeAriaLabel={closeAriaLabel}
                        paper={paper}
                        border={border}
                        textSecondary={textSecondary}
                    />

                    {statusBanner}

                    <MuiMaterials.Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            position: "relative",
                            // Gray page canvas so ModalContentTile margins read like Site Grades.
                            backgroundColor: pageBg,
                            color: textPrimary,
                            WebkitOverflowScrolling: "touch",
                        }}
                    >
                        {loadingOverlay}
                        {children}
                    </MuiMaterials.Box>
                </div>
            </MuiMaterials.ThemeProvider>
        </MuiMaterials.Modal>
    );
}
